/**
 * Credentials Panel - Secrets Vault for IT Operations
 * 
 * Features:
 * - List credentials with masked values
 * - Reveal secrets with audit logging
 * - CRUD operations for Admin/IT Manager
 * - View-only for IT Support
 * - Rotation tracking and alerts
 * - Asset linking
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useDebouncedSearch } from '../../hooks/useDebouncedSearch';
import {
  Key, Eye, EyeOff, Plus, Edit2, Trash2, RefreshCw, Search,
  Shield, AlertTriangle, Clock, Link2, Copy, Check, Filter,
  Server, Cloud, Globe, Monitor, Settings, FileKey, ChevronDown,
  RotateCcw, Calendar, Tag, ExternalLink, Lock, Unlock, RotateCw,
  Archive, Undo2
} from 'lucide-react';
import { toast } from 'sonner';
import ITPagination from '../it/ITPagination';
import './credentials.css';
import { formatDateOnly } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Category icons mapping
const CATEGORY_ICONS = {
  infrastructure: Server,
  saas: Cloud,
  network: Globe,
  devices: Monitor,
  internal_systems: Settings,
  licenses: FileKey
};

// Get auth token
const getToken = () => localStorage.getItem('token');

// API helper
const apiFetch = async (endpoint, options = {}) => {
  const token = getToken();
  const response = await fetch(`${API}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers
    }
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }
  
  return response.json();
};

// Credential Card Component
const CredentialCard = ({ credential, accessLevel, canWrite, canReveal, onEdit, onDelete, onReveal, onRotate, onRestore }) => {
  const [revealedFields, setRevealedFields] = useState({});
  const [copying, setCopying] = useState(null);
  const [revealing, setRevealing] = useState(null);
  
  const CategoryIcon = CATEGORY_ICONS[credential.category] || Key;
  const isDeleted = credential.is_deleted;
  
  const handleReveal = async (field) => {
    if (isDeleted) return; // Can't reveal deleted credentials
    if (revealedFields[field]) {
      // Hide the field
      setRevealedFields(prev => ({ ...prev, [field]: null }));
      return;
    }
    
    try {
      setRevealing(field);
      const result = await onReveal(credential.id, field);
      setRevealedFields(prev => ({ ...prev, [field]: result.value }));
      
      // Auto-hide after 30 seconds
      setTimeout(() => {
        setRevealedFields(prev => ({ ...prev, [field]: null }));
      }, 30000);
    } catch (err) {
      toast.error(`Failed to reveal ${field}: ${err.message}`);
    } finally {
      setRevealing(null);
    }
  };
  
  const handleCopy = async (value, fieldName) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopying(fieldName);
      toast.success(`${fieldName} copied to clipboard`);
      setTimeout(() => setCopying(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };
  
  const getRotationBadge = () => {
    if (!credential.rotation_status) return null;
    
    const { is_overdue, is_due_soon, days_until_rotation } = credential.rotation_status;
    
    if (is_overdue) {
      return (
        <span className="rotation-badge overdue">
          <AlertTriangle size={12} />
          Overdue by {Math.abs(days_until_rotation)} days
        </span>
      );
    }
    
    if (is_due_soon) {
      return (
        <span className="rotation-badge due-soon">
          <Clock size={12} />
          Due in {days_until_rotation} days
        </span>
      );
    }
    
    return null;
  };
  
  const renderSecretField = (fieldName, maskedValue, label) => {
    if (!maskedValue) return null;
    
    const isRevealed = revealedFields[fieldName];
    const displayValue = isRevealed || maskedValue;
    
    return (
      <div className="secret-field">
        <span className="field-label">{label}:</span>
        <div className="field-value-container">
          <code className={`field-value ${isRevealed ? 'revealed' : 'masked'}`}>
            {displayValue}
          </code>
          <div className="field-actions">
            {(accessLevel === 'full' || canReveal) && !isDeleted && (
              <>
                <button
                  className="btn-icon-small"
                  data-testid={`credential-reveal-btn-${fieldName}`}
                  onClick={() => handleReveal(fieldName)}
                  disabled={revealing === fieldName}
                  title={isRevealed ? 'Hide' : 'Reveal'}
                >
                  {revealing === fieldName ? (
                    <RefreshCw size={14} className="spinning" />
                  ) : isRevealed ? (
                    <EyeOff size={14} />
                  ) : (
                    <Eye size={14} />
                  )}
                </button>
                {isRevealed && (
                  <button
                    className="btn-icon-small"
                    onClick={() => handleCopy(displayValue, label)}
                    title="Copy"
                  >
                    {copying === label ? <Check size={14} /> : <Copy size={14} />}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div className={`credential-card cvp-card-fade ${isDeleted ? 'deleted' : ''}`} data-testid={`credential-${credential.id}`}>
      {isDeleted && (
        <div className="deleted-banner">
          <Trash2 size={14} />
          <span>Deleted on {formatDateOnly(credential.deleted_at)}</span>
        </div>
      )}
      <div className="credential-header">
        <div className="credential-icon">
          <CategoryIcon size={20} />
        </div>
        <div className="credential-title">
          <h4>{credential.name}</h4>
          <span className="credential-type">{credential.credential_type?.replace('_', ' ')}</span>
        </div>
        <div className="credential-badges">
          {getRotationBadge()}
          {!isDeleted && !credential.rotation_status?.is_overdue && !credential.rotation_status?.is_due_soon && (
            <span className="cvp-card-health"><span className="cvp-health-dot" /> Healthy</span>
          )}
          <span className={`category-badge ${credential.category}`}>
            {credential.category?.replace('_', ' ')}
          </span>
        </div>
      </div>
      
      {credential.description && (
        <p className="credential-description">{credential.description}</p>
      )}
      
      <div className="credential-fields">
        {credential.username && (
          <div className="info-field">
            <span className="field-label">Username:</span>
            <span className="field-value">{credential.username}</span>
            <button
              className="btn-icon-small"
              onClick={() => handleCopy(credential.username, 'Username')}
              title="Copy"
            >
              {copying === 'Username' ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
        )}
        
        {credential.url && (
          <div className="info-field">
            <span className="field-label">URL:</span>
            <a href={credential.url} target="_blank" rel="noopener noreferrer" className="field-link">
              {credential.url}
              <ExternalLink size={12} />
            </a>
          </div>
        )}
        
        {renderSecretField('password', credential.password_masked, 'Password')}
        {renderSecretField('api_key', credential.api_key_masked, 'API Key')}
        {renderSecretField('secret_value', credential.secret_value_masked, 'Secret')}
      </div>
      
      {credential.linked_assets && credential.linked_assets.length > 0 && (
        <div className="linked-assets">
          <span className="linked-label">
            <Link2 size={14} /> Linked Assets:
          </span>
          <div className="asset-chips">
            {credential.linked_assets.map(asset => (
              <span key={asset.id} className="asset-chip">
                {asset.asset_tag || asset.name}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {credential.tags && credential.tags.length > 0 && (
        <div className="credential-tags">
          {credential.tags.map(tag => (
            <span key={tag} className="tag-chip">
              <Tag size={10} /> {tag}
            </span>
          ))}
        </div>
      )}
      
      <div className="credential-footer">
        <div className="credential-meta">
          <span className="meta-item">
            <Calendar size={12} />
            Created {formatDateOnly(credential.created_at)}
          </span>
          {credential.last_rotated && !isDeleted && (
            <span className="meta-item">
              <RotateCcw size={12} />
              Rotated {formatDateOnly(credential.last_rotated)}
            </span>
          )}
        </div>
        
        {(accessLevel === 'full' || canWrite) && !isDeleted && (
          <div className="credential-actions">
            {accessLevel === 'full' && (
              <button
                className="btn-action rotate"
                data-testid={`credential-rotate-btn-${credential.id}`}
                onClick={() => onRotate(credential)}
                title="Rotate Credential"
              >
                <RotateCcw size={14} />
              </button>
            )}
            <button
              className="btn-action edit"
              data-testid={`credential-edit-btn-${credential.id}`}
              onClick={() => onEdit(credential)}
              title="Edit"
            >
              <Edit2 size={14} />
            </button>
            {accessLevel === 'full' && (
              <button
                className="btn-action delete"
                data-testid={`credential-delete-btn-${credential.id}`}
                onClick={() => onDelete(credential)}
                title="Delete"
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        )}
        
        {accessLevel === 'full' && isDeleted && (
          <div className="credential-actions">
            <button
              className="btn-action restore"
              onClick={() => onRestore(credential)}
              title="Restore Credential"
            >
              <Undo2 size={14} />
            </button>
            <button
              className="btn-action delete-permanent"
              onClick={() => onDelete(credential, true)}
              title="Permanently Delete"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Dynamic fields config based on credential type
const CREDENTIAL_FIELD_CONFIG = {
  username_password: { username: true, password: true, api_key: false, secret_value: false },
  api_key: { username: false, password: false, api_key: true, secret_value: true },
  ssh_key: { username: true, password: false, api_key: false, secret_value: true }, // secret_value = private key
  certificate: { username: false, password: false, api_key: false, secret_value: true }, // secret_value = cert content
  token: { username: false, password: false, api_key: false, secret_value: true }, // secret_value = token
  license_key: { username: false, password: false, api_key: false, secret_value: true }, // secret_value = license
  other: { username: true, password: true, api_key: true, secret_value: true } // show all
};

// Field labels based on credential type
const FIELD_LABELS = {
  ssh_key: { secret_value: 'Private Key' },
  certificate: { secret_value: 'Certificate Content' },
  token: { secret_value: 'Access Token' },
  license_key: { secret_value: 'License Key' }
};

// Add/Edit Credential Modal
const CredentialModal = ({ isOpen, onClose, credential, categories, types, assets, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    credential_type: '',
    username: '',
    password: '',
    api_key: '',
    secret_value: '',
    url: '',
    notes: '',
    linked_asset_ids: [],
    rotation_policy_days: null,
    tags: []
  });
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState({});
  // CVM-REDESIGN: local reveal toggles for secret inputs (UI only)
  const [showSecret, setShowSecret] = useState({});
  
  useEffect(() => {
    if (credential) {
      setFormData({
        name: credential.name || '',
        description: credential.description || '',
        category: credential.category || 'infrastructure',
        credential_type: credential.credential_type || 'username_password',
        username: credential.username || '',
        password: '', // Don't pre-fill sensitive fields
        api_key: '',
        secret_value: '',
        url: credential.url || '',
        notes: credential.notes || '',
        linked_asset_ids: credential.linked_asset_ids || [],
        rotation_policy_days: credential.rotation_policy_days || null,
        tags: credential.tags || []
      });
    } else {
      setFormData({
        name: '',
        description: '',
        category: 'infrastructure',
        credential_type: 'username_password',
        username: '',
        password: '',
        api_key: '',
        secret_value: '',
        url: '',
        notes: '',
        linked_asset_ids: [],
        rotation_policy_days: null,
        tags: []
      });
    }
    setErrors({});
  }, [credential, isOpen]);
  
  // Get which fields to show based on credential type
  const fieldConfig = CREDENTIAL_FIELD_CONFIG[formData.credential_type] || CREDENTIAL_FIELD_CONFIG.other;
  const fieldLabels = FIELD_LABELS[formData.credential_type] || {};
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.category) {
      newErrors.category = 'Category is required';
    }
    if (!formData.credential_type) {
      newErrors.credential_type = 'Credential type is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // URL normalization (Aug 24, 2026): accept www.example.com / example.com
    // by prepending https://, but still reject genuinely malformed values.
    let normalizedUrl = (formData.url || '').trim();
    if (normalizedUrl) {
      if (!/^https?:\/\//i.test(normalizedUrl)) {
        normalizedUrl = 'https://' + normalizedUrl;
      }
      let valid = false;
      try {
        const parsed = new URL(normalizedUrl);
        valid = ['http:', 'https:'].includes(parsed.protocol) &&
                /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i.test(parsed.hostname);
      } catch (err) {
        valid = false;
      }
      if (!valid) {
        setErrors(prev => ({ ...prev, url: 'Please enter a valid website (e.g. www.example.com)' }));
        toast.error('Please enter a valid website URL (e.g. www.example.com)');
        return;
      }
    }
    
    try {
      setSaving(true);
      await onSave({ ...formData, url: normalizedUrl }, credential?.id);
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };
  
  const addTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };
  
  const removeTag = (tag) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };
  
  // Check if form is valid for submit button
  const isFormValid = formData.name.trim() && formData.category && formData.credential_type;

  const toggleSecret = (field) => setShowSecret(prev => ({ ...prev, [field]: !prev[field] }));

  const nextRotationDisplay = formData.rotation_policy_days
    ? formatDateOnly(new Date(Date.now() + formData.rotation_policy_days * 86400000))
    : null;
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-overlay cvm-overlay" onClick={onClose}>
      <div className="cvm-modal" onClick={e => e.stopPropagation()} data-testid="credential-modal">
        {/* Fixed header */}
        <div className="cvm-header">
          <div className="cvm-header-left">
            <div className="cvm-header-icon"><Key size={18} /></div>
            <div>
              <h3>{credential ? 'Edit Credential' : 'Add New Credential'}</h3>
              <p>Securely store and manage access credentials</p>
            </div>
          </div>
          <div className="cvm-header-right">
            <span className="cvm-encrypted-chip"><Lock size={11} /> Encrypted at Rest</span>
            <button type="button" className="cvm-close" onClick={onClose} data-testid="modal-close-btn" aria-label="Close">×</button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="cvm-form">
          {/* Scrollable body */}
          <div className="cvm-body">
            {/* BASIC INFORMATION */}
            <div className="cvm-section">
              <div className="cvm-section-title">Basic Information</div>
              <div className="cvm-row">
                <div className="cvm-field">
                  <label>Credential Name <span className="cvm-req">*</span></label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Production Database"
                    className={errors.name ? 'cvm-input-error' : ''}
                    data-testid="credential-name-input"
                  />
                  {errors.name && <span className="cvm-error">{errors.name}</span>}
                </div>
                <div className="cvm-field">
                  <label>Category <span className="cvm-req">*</span></label>
                  <select
                    value={formData.category}
                    onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
                    className={errors.category ? 'cvm-input-error' : ''}
                    data-testid="credential-category-select"
                  >
                    <option value="">-- Select Category --</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                    ))}
                  </select>
                  {errors.category && <span className="cvm-error">{errors.category}</span>}
                </div>
              </div>
              <div className="cvm-row">
                <div className="cvm-field">
                  <label>Credential Type <span className="cvm-req">*</span></label>
                  <select
                    value={formData.credential_type}
                    onChange={e => setFormData(prev => ({ ...prev, credential_type: e.target.value }))}
                    className={errors.credential_type ? 'cvm-input-error' : ''}
                    data-testid="credential-type-select"
                  >
                    <option value="">-- Select Type --</option>
                    {types.map(type => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                  {errors.credential_type && <span className="cvm-error">{errors.credential_type}</span>}
                </div>
                <div className="cvm-field">
                  <label>URL</label>
                  <input
                    type="text"
                    inputMode="url"
                    value={formData.url}
                    onChange={e => setFormData(prev => ({ ...prev, url: e.target.value }))}
                    placeholder="www.example.com or https://example.com"
                    data-testid="credential-url-input"
                  />
                  {errors.url && <span className="cvm-error">{errors.url}</span>}
                </div>
              </div>
              <div className="cvm-field">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="What is this credential for?"
                  rows={2}
                  data-testid="credential-description-input"
                />
              </div>
            </div>
            
            {/* SECRET VALUES */}
            <div className="cvm-section cvm-section-secure">
              <div className="cvm-section-title"><Lock size={12} /> Secret Values</div>
              <p className="cvm-section-hint">Secret values are encrypted and access is audited.{credential ? ' Leave blank to keep existing values.' : ''} Fields shown depend on credential type.</p>
              
              <div className="cvm-row">
                {fieldConfig.username && (
                  <div className="cvm-field">
                    <label>Username</label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={e => setFormData(prev => ({ ...prev, username: e.target.value }))}
                      placeholder="Username or email"
                      data-testid="credential-username-input"
                    />
                  </div>
                )}
                {fieldConfig.password && (
                  <div className="cvm-field">
                    <label>Password</label>
                    <div className="cvm-secret-wrap">
                      <input
                        type={showSecret.password ? 'text' : 'password'}
                        value={formData.password}
                        onChange={e => setFormData(prev => ({ ...prev, password: e.target.value }))}
                        placeholder="••••••••••••••••"
                        data-testid="credential-password-input"
                      />
                      <button type="button" className="cvm-eye" onClick={() => toggleSecret('password')} title={showSecret.password ? 'Hide' : 'Reveal'} data-testid="credential-password-toggle">
                        {showSecret.password ? <EyeOff size={15} /> : <Eye size={15} />}
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              {fieldConfig.api_key && (
                <div className="cvm-field">
                  <label>API Key</label>
                  <div className="cvm-secret-wrap">
                    <input
                      type={showSecret.api_key ? 'text' : 'password'}
                      value={formData.api_key}
                      onChange={e => setFormData(prev => ({ ...prev, api_key: e.target.value }))}
                      placeholder="••••••••••••••••"
                      data-testid="credential-apikey-input"
                    />
                    <button type="button" className="cvm-eye" onClick={() => toggleSecret('api_key')} title={showSecret.api_key ? 'Hide' : 'Reveal'}>
                      {showSecret.api_key ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </div>
                </div>
              )}
              
              {fieldConfig.secret_value && (
                <div className="cvm-field">
                  <label>{fieldLabels.secret_value || 'Secret Value'}</label>
                  <textarea
                    value={formData.secret_value}
                    onChange={e => setFormData(prev => ({ ...prev, secret_value: e.target.value }))}
                    placeholder={formData.credential_type === 'ssh_key' ? 'Paste private key here...' : '••••••••'}
                    rows={formData.credential_type === 'ssh_key' || formData.credential_type === 'certificate' ? 4 : 1}
                    data-testid="credential-secret-input"
                  />
                </div>
              )}
            </div>
            
            {/* ROTATION & SECURITY */}
            <div className="cvm-section">
              <div className="cvm-section-title"><RotateCcw size={12} /> Rotation &amp; Security</div>
              <div className="cvm-rotation-card">
                <div className="cvm-field cvm-rotation-input">
                  <label>Rotation Period (Days)</label>
                  <input
                    type="number"
                    value={formData.rotation_policy_days || ''}
                    onChange={e => setFormData(prev => ({ 
                      ...prev, 
                      rotation_policy_days: e.target.value ? parseInt(e.target.value) : null 
                    }))}
                    placeholder="e.g., 90"
                    min={1}
                    max={365}
                  />
                </div>
                <div className="cvm-rotation-info">
                  <p>We'll flag this credential when rotation is due.</p>
                  {nextRotationDisplay ? (
                    <span className="cvm-next-rotation"><Clock size={12} /> Next rotation: {nextRotationDisplay}</span>
                  ) : (
                    <span className="cvm-next-rotation muted">Leave empty for no rotation tracking</span>
                  )}
                </div>
              </div>
            </div>
            
            {/* LINKS & METADATA */}
            <div className="cvm-section">
              <div className="cvm-section-title"><Link2 size={12} /> Links &amp; Metadata</div>
              <div className="cvm-field">
                <label>Linked Assets</label>
                <select
                  multiple
                  value={formData.linked_asset_ids}
                  onChange={e => setFormData(prev => ({ 
                    ...prev, 
                    linked_asset_ids: Array.from(e.target.selectedOptions, opt => opt.value)
                  }))}
                  className="cvm-multi"
                >
                  {assets.map(asset => (
                    <option key={asset.id} value={asset.id}>
                      {asset.asset_tag} - {asset.name}
                    </option>
                  ))}
                </select>
                <span className="cvm-hint">Hold Ctrl/Cmd to select multiple</span>
              </div>
              <div className="cvm-field">
                <label>Tags</label>
                <div className="cvm-tag-input">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={e => setTagInput(e.target.value)}
                    onKeyPress={e => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Add a tag..."
                  />
                  <button type="button" className="cvm-btn-tag" onClick={addTag}>Add</button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="cvm-tags">
                    {formData.tags.map(tag => (
                      <span key={tag} className="cvm-tag">
                        {tag}
                        <button type="button" onClick={() => removeTag(tag)}>×</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="cvm-field">
                <label>Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={e => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Additional notes..."
                  rows={2}
                />
              </div>
            </div>
          </div>
          
          {/* Sticky footer */}
          <div className="cvm-footer">
            <button type="button" className="cvm-btn-cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="cvm-btn-save" disabled={saving || !isFormValid} data-testid="credential-save-btn">
              {saving ? <RefreshCw size={15} className="spinning" /> : <Lock size={15} />}
              {credential ? 'Update Credential' : 'Save Credential'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Rotate Credential Modal
const RotateModal = ({ isOpen, onClose, credential, onRotate }) => {
  const [newPassword, setNewPassword] = useState('');
  const [newApiKey, setNewApiKey] = useState('');
  const [newSecretValue, setNewSecretValue] = useState('');
  const [rotating, setRotating] = useState(false);
  
  const handleRotate = async () => {
    if (!newPassword && !newApiKey && !newSecretValue) {
      toast.error('Please provide at least one new value');
      return;
    }
    
    try {
      setRotating(true);
      await onRotate(credential.id, { newPassword, newApiKey, newSecretValue });
      toast.success('Credential rotated successfully');
      onClose();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRotating(false);
    }
  };
  
  if (!isOpen || !credential) return null;
  
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="rotate-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3><RotateCcw size={20} /> Rotate Credential</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <p className="rotate-info">
            Rotating <strong>{credential.name}</strong>. Enter new values for the fields you want to rotate.
          </p>
          
          {credential.password_masked && (
            <div className="form-group">
              <label>New Password</label>
              <input
                type="password"
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />
            </div>
          )}
          
          {credential.api_key_masked && (
            <div className="form-group">
              <label>New API Key</label>
              <input
                type="password"
                value={newApiKey}
                onChange={e => setNewApiKey(e.target.value)}
                placeholder="Enter new API key"
              />
            </div>
          )}
          
          {credential.secret_value_masked && (
            <div className="form-group">
              <label>New Secret Value</label>
              <input
                type="password"
                value={newSecretValue}
                onChange={e => setNewSecretValue(e.target.value)}
                placeholder="Enter new secret value"
              />
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-rotate" onClick={handleRotate} disabled={rotating}>
            {rotating ? <RefreshCw size={16} className="spinning" /> : <RotateCcw size={16} />}
            Rotate Now
          </button>
        </div>
      </div>
    </div>
  );
};

// CVP-REDESIGN: soft count-up for KPI numbers
const CountUp = ({ value = 0 }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!value) { setN(0); return; }
    let start = null;
    let raf;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min((t - start) / 600, 1);
      setN(Math.round(p * value));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{n}</>;
};

// Main Credentials Panel
const CredentialsPanel = () => {
  const [credentials, setCredentials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [assets, setAssets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessLevel, setAccessLevel] = useState('none');
  // IT-SUPPORT-PERM-001: IT Support may add/edit (backend can_write flag)
  const [canWrite, setCanWrite] = useState(false);
  // IT-SUPPORT-PERM-002: IT Support may reveal secrets (backend can_reveal flag)
  const [canReveal, setCanReveal] = useState(false);
  
  // Filters
  const { searchInput: searchTerm, setSearchInput: setSearchTerm, debouncedValue: debouncedSearchTerm } = useDebouncedSearch('', 350);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [credLimit, setCredLimit] = useState(25);
  const [credTotal, setCredTotal] = useState(0);
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCredential, setEditingCredential] = useState(null);
  const [rotatingCredential, setRotatingCredential] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Fetch data
  const fetchCredentials = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: credLimit });
      if (selectedCategory) params.append('category', selectedCategory);
      if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
      if (showDeleted) params.append('include_deleted', 'true');
      
      const data = await apiFetch(`/api/it-credentials/?${params}`);
      setCredentials(data.credentials);
      setTotalPages(data.pages);
      setCredTotal(data.total || 0);
      setAccessLevel(data.access_level);
      setCanWrite(Boolean(data.can_write ?? data.access_level === 'full'));
      setCanReveal(Boolean(data.can_reveal ?? data.access_level === 'full'));
    } catch (err) {
      toast.error('Failed to load credentials');
    } finally {
      setLoading(false);
    }
  }, [page, credLimit, selectedCategory, debouncedSearchTerm, showDeleted]);
  
  const fetchMetadata = useCallback(async () => {
    try {
      // Fetch categories and types first (required for modal)
      const [catData, typeData, statsData] = await Promise.all([
        apiFetch('/api/it-credentials/categories'),
        apiFetch('/api/it-credentials/types'),
        apiFetch('/api/it-credentials/stats/summary')
      ]);
      
      setCategories(catData.categories || []);
      setTypes(typeData.types || []);
      setStats(statsData);
      
      // Assets are optional, fetch separately to not block the main data
      try {
        const assetsData = await apiFetch('/api/it-assets?limit=100');
        setAssets(assetsData.assets || []);
      } catch (assetErr) {
        console.warn('Failed to load assets for linking:', assetErr);
        setAssets([]);
      }
    } catch (err) {
      console.error('Failed to load metadata:', err);
      // Even if main fetch fails, try to get categories and types individually
      try {
        const catData = await apiFetch('/api/it-credentials/categories');
        setCategories(catData.categories || []);
      } catch (e) { setCategories([]); }
      
      try {
        const typeData = await apiFetch('/api/it-credentials/types');
        setTypes(typeData.types || []);
      } catch (e) { setTypes([]); }
    }
  }, []);
  
  useEffect(() => {
    fetchCredentials();
  }, [fetchCredentials]);
  
  useEffect(() => {
    fetchMetadata();
  }, [fetchMetadata]);
  
  // Handlers
  const handleReveal = async (credentialId, field) => {
    return await apiFetch(`/api/it-credentials/${credentialId}/reveal?field=${field}`, {
      method: 'POST'
    });
  };
  
  const handleSave = async (formData, credentialId) => {
    const endpoint = credentialId 
      ? `/api/it-credentials/${credentialId}`
      : '/api/it-credentials/';
    
    const method = credentialId ? 'PUT' : 'POST';
    
    // Clean empty strings for optional fields
    const cleanData = { ...formData };
    if (!cleanData.password) delete cleanData.password;
    if (!cleanData.api_key) delete cleanData.api_key;
    if (!cleanData.secret_value) delete cleanData.secret_value;
    
    await apiFetch(endpoint, { method, body: JSON.stringify(cleanData) });
    toast.success(credentialId ? 'Credential updated' : 'Credential created');
    fetchCredentials();
    fetchMetadata();
  };
  
  const handleRotate = async (credentialId, { newPassword, newApiKey, newSecretValue }) => {
    await apiFetch(`/api/it-credentials/${credentialId}/rotate`, {
      method: 'POST',
      body: JSON.stringify({
        new_password: newPassword || null,
        new_api_key: newApiKey || null,
        new_secret_value: newSecretValue || null
      })
    });
    fetchCredentials();
    fetchMetadata();
  };
  
  const handleDelete = async (credential, permanent = false) => {
    try {
      const endpoint = permanent 
        ? `/api/it-credentials/${credential.id}?permanent=true`
        : `/api/it-credentials/${credential.id}`;
      await apiFetch(endpoint, { method: 'DELETE' });
      toast.success(permanent ? 'Credential permanently deleted' : 'Credential moved to trash');
      setDeleteConfirm(null);
      fetchCredentials();
      fetchMetadata();
    } catch (err) {
      toast.error(err.message);
    }
  };
  
  const handleRestore = async (credential) => {
    try {
      await apiFetch(`/api/it-credentials/${credential.id}/restore`, { method: 'POST' });
      toast.success('Credential restored');
      fetchCredentials();
      fetchMetadata();
    } catch (err) {
      toast.error(err.message);
    }
  };
  
  return (
    <div className="credentials-panel cvp-panel" data-testid="credentials-panel">
      {/* Header */}
      <div className="cvp-header">
        <div className="cvp-header-title">
          <div className="cvp-shield"><Shield size={22} /></div>
          <div>
            <h2>Credential Vault</h2>
            <p>Securely manage privileged credentials, secrets and access rotation</p>
          </div>
        </div>
        <div className="cvp-header-actions">
          <span className="cvp-security-badge"><Lock size={11} /> Encrypted &amp; Audited</span>
          {(accessLevel === 'full' || canWrite) && (
            <button className="cvp-btn-add" onClick={() => setShowAddModal(true)} data-testid="cvp-add-credential-btn">
              <Plus size={16} />
              Add Credential
            </button>
          )}
        </div>
      </div>
      
      {/* KPI Cards */}
      {stats && (() => {
        const vaultState = stats.rotation_overdue > 0 ? 'risk' : stats.rotation_due_soon > 0 ? 'warn' : 'secure';
        return (
        <div className="cvp-kpis" data-testid="cvp-kpis">
          <div className="cvp-kpi">
            <div className="cvp-kpi-head"><Key size={14} /> Total Credentials</div>
            <div className="cvp-kpi-value"><CountUp value={stats.total_credentials} /></div>
            <div className="cvp-kpi-sub">Credentials securely stored</div>
          </div>
          <div className={`cvp-kpi ${stats.rotation_overdue > 0 ? 'cvp-kpi-danger' : ''}`}>
            <div className="cvp-kpi-head"><AlertTriangle size={14} /> Rotation Overdue</div>
            <div className="cvp-kpi-value"><CountUp value={stats.rotation_overdue} /></div>
            <div className="cvp-kpi-sub">{stats.rotation_overdue > 0 ? 'Requires attention' : 'Nothing overdue'}</div>
          </div>
          <div className={`cvp-kpi ${stats.rotation_due_soon > 0 ? 'cvp-kpi-warn' : ''}`}>
            <div className="cvp-kpi-head"><Clock size={14} /> Due Soon</div>
            <div className="cvp-kpi-value"><CountUp value={stats.rotation_due_soon} /></div>
            <div className="cvp-kpi-sub">Next 7 days</div>
          </div>
          <div className={`cvp-kpi cvp-kpi-health cvp-health-${vaultState}`} data-testid="cvp-vault-health">
            <div className="cvp-kpi-head"><Shield size={14} /> Vault Health</div>
            <div className="cvp-kpi-value cvp-health-value">
              <span className="cvp-health-dot" />
              {vaultState === 'secure' ? 'SECURE' : vaultState === 'warn' ? 'ATTENTION' : 'AT RISK'}
            </div>
            <div className="cvp-kpi-sub">
              {vaultState === 'secure' ? 'No security issues detected'
                : vaultState === 'warn' ? `${stats.rotation_due_soon} rotation${stats.rotation_due_soon === 1 ? '' : 's'} due soon`
                : `${stats.rotation_overdue} rotation${stats.rotation_overdue === 1 ? '' : 's'} overdue`}
            </div>
          </div>
        </div>
        );
      })()}
      
      {/* Toolbar */}
      <div className="cvp-toolbar" data-testid="cvp-toolbar">
        <div className="cvp-search">
          <Search size={15} />
          <input
            type="text"
            placeholder="Search credentials..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        <select 
          className="cvp-select"
          value={selectedCategory} 
          onChange={e => { setSelectedCategory(e.target.value); setPage(1); }}
        >
          <option value="">All Categories</option>
          {categories.map(cat => (
            <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
          ))}
        </select>
        {accessLevel === 'full' && (
          <select
            className="cvp-select"
            value={showDeleted ? 'all' : 'active'}
            onChange={e => { setShowDeleted(e.target.value === 'all'); setPage(1); }}
            data-testid="cvp-status-filter"
          >
            <option value="active">Active</option>
            <option value="all">All (incl. Deleted)</option>
          </select>
        )}
        <button className="cvp-refresh" onClick={fetchCredentials} title="Refresh">
          <RefreshCw size={15} />
        </button>
      </div>
      
      {/* Access Level Banner */}
      {/* UI cleanup (Aug 24, 2026): no banner for operational access
          (add/edit/reveal) — permissions are enforced by the app. Banner
          remains only for genuinely restricted view-only sessions. */}
      {accessLevel === 'view_only' && !(canWrite && canReveal) && (
        <div className="access-banner">
          <EyeOff size={16} />
          <span>
            {canWrite
              ? 'Limited access: you can add and edit credentials, but secret values remain masked and cannot be revealed.'
              : 'View-only access. Secret values are masked and cannot be revealed.'}
          </span>
        </div>
      )}
      
      {/* Credentials Grid */}
      <div className="credentials-grid cvp-grid">
        {loading ? (
          <div className="loading-state">
            <RefreshCw size={32} className="spinning" />
            <p>Loading credentials...</p>
          </div>
        ) : credentials.length === 0 ? (
          <div className="cvp-empty" data-testid="cvp-empty-state">
            <div className="cvp-empty-icon"><Shield size={30} /></div>
            <h3>{searchTerm || selectedCategory ? 'No Matching Credentials' : 'Your Credential Vault is Empty'}</h3>
            <p>
              {searchTerm || selectedCategory 
                ? 'Try adjusting your search or filters.'
                : 'Securely store application, infrastructure, database and service credentials in one protected location.'}
            </p>
            {(accessLevel === 'full' || canWrite) && !searchTerm && !selectedCategory && (
              <button className="cvp-btn-add" onClick={() => setShowAddModal(true)}>
                <Plus size={15} /> Add Your First Credential
              </button>
            )}
            <div className="cvp-trust-row">
              <span><Lock size={12} /> Encrypted at Rest</span>
              <span><Eye size={12} /> Access Controlled</span>
              <span><Shield size={12} /> Activity Audited</span>
            </div>
          </div>
        ) : (
          credentials.map(cred => (
            <CredentialCard
              key={cred.id}
              credential={cred}
              accessLevel={accessLevel}
              canWrite={canWrite}
              canReveal={canReveal}
              onEdit={c => { setEditingCredential(c); setShowAddModal(true); }}
              onDelete={(c, permanent) => permanent ? handleDelete(c, true) : setDeleteConfirm(c)}
              onReveal={handleReveal}
              onRotate={c => setRotatingCredential(c)}
              onRestore={handleRestore}
            />
          ))
        )}
      </div>
      
      {/* Pagination */}
      <ITPagination
        page={page}
        limit={credLimit}
        total={credTotal}
        onPageChange={(p) => setPage(p)}
        onLimitChange={(l) => { setCredLimit(l); setPage(1); }}
        testid="credentials-pagination"
      />
      
      {/* Modals */}
      <CredentialModal
        isOpen={showAddModal}
        onClose={() => { setShowAddModal(false); setEditingCredential(null); }}
        credential={editingCredential}
        categories={categories}
        types={types}
        assets={assets}
        onSave={handleSave}
      />
      
      <RotateModal
        isOpen={!!rotatingCredential}
        onClose={() => setRotatingCredential(null)}
        credential={rotatingCredential}
        onRotate={handleRotate}
      />
      
      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="delete-confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3><AlertTriangle size={20} /> Delete Credential</h3>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete <strong>{deleteConfirm.name}</strong>?</p>
              <p className="warning-text">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn-delete" onClick={() => handleDelete(deleteConfirm)}>
                <Trash2 size={16} /> Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CredentialsPanel;
